import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Check,
  Pencil,
  Plus,
  ShieldCheck,
  Tag,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { apiService, extractApiError } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useFeatureRequestTopics } from "../hooks/useFeatureRequestTopics";
import { useFeatureRequestTeams } from "../hooks/useFeatureRequestTeams";
import LoadingSpinner from "./LoadingSpinner";

interface FeatureRequestPowerUser {
  id: string;
  email: string;
  grantedByEmail: string;
  grantedAt: string;
}

interface SectionProps {
  icon: typeof Tag;
  title: string;
  description: string;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, description, children }: SectionProps) {
  return (
    <section className="card">
      <div className="mb-5 flex items-start gap-3">
        <div className="rounded-full bg-primary-100 p-2 text-primary-600 dark:bg-primary-900 dark:text-primary-400">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>
      </div>
      {children}
    </section>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
      {children}
    </p>
  );
}

function DeleteButton({
  onClick,
  disabled,
  label,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="btn-icon shrink-0 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:border-red-800 dark:hover:bg-red-950 dark:hover:text-red-400"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

export default function FeatureRequestManagePanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.roleId === "global_admin";

  const { topics, isLoading: topicsLoading } = useFeatureRequestTopics();
  const { teams, isLoading: teamsLoading } = useFeatureRequestTeams();

  const [newTopic, setNewTopic] = useState("");
  const [newTopicTeam, setNewTopicTeam] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [memberDrafts, setMemberDrafts] = useState<Record<string, string>>({});
  const [editingTopic, setEditingTopic] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({ name: "", color: "" });

  const powerUsersQuery = useQuery<FeatureRequestPowerUser[]>(
    "feature-request-power-users",
    async () => {
      const res = await apiService.getFeatureRequestPowerUsers();
      return (res.data?.data?.items ?? []) as FeatureRequestPowerUser[];
    },
  );

  const refreshTopics = () => {
    queryClient.invalidateQueries("feature-request-topics");
    queryClient.invalidateQueries("feature-request-teams");
    queryClient.invalidateQueries("feature-requests");
  };

  const onError = (err: unknown) => {
    toast.error(extractApiError(err));
  };

  const createTopicMutation = useMutation(
    () =>
      apiService.createFeatureRequestTopic(
        newTopic.trim(),
        newTopicTeam || null,
      ),
    {
      onSuccess: () => {
        setNewTopic("");
        refreshTopics();
        toast.success("Topic created");
      },
      onError,
    },
  );

  const deleteTopicMutation = useMutation(
    ({ slug, reassign }: { slug: string; reassign: boolean }) =>
      apiService.deleteFeatureRequestTopic(slug, reassign),
    {
      onSuccess: () => {
        refreshTopics();
        toast.success("Topic removed");
      },
      onError,
    },
  );

  const updateTopicMutation = useMutation(
    ({ slug, name, color }: { slug: string; name: string; color: string }) =>
      apiService.updateFeatureRequestTopic(slug, name, color),
    {
      onSuccess: () => {
        setEditingTopic(null);
        refreshTopics();
        toast.success("Topic updated");
      },
      onError,
    },
  );

  const assignTeamMutation = useMutation(
    ({ slug, teamId }: { slug: string; teamId: string | null }) =>
      apiService.assignFeatureRequestTopicTeam(slug, teamId),
    {
      onSuccess: () => {
        refreshTopics();
        toast.success("Topic moved");
      },
      onError,
    },
  );

  const createTeamMutation = useMutation(
    () => apiService.createFeatureRequestTeam(newTeam.trim()),
    {
      onSuccess: () => {
        setNewTeam("");
        queryClient.invalidateQueries("feature-request-teams");
        toast.success("Team created");
      },
      onError,
    },
  );

  const deleteTeamMutation = useMutation(
    (id: string) => apiService.deleteFeatureRequestTeam(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("feature-request-teams");
        toast.success("Team deleted");
      },
      onError,
    },
  );

  const addMemberMutation = useMutation(
    ({ id, email }: { id: string; email: string }) =>
      apiService.addFeatureRequestTeamMember(id, email),
    {
      onSuccess: (_data, variables) => {
        setMemberDrafts((d) => ({ ...d, [variables.id]: "" }));
        queryClient.invalidateQueries("feature-request-teams");
        toast.success("Member added");
      },
      onError,
    },
  );

  const removeMemberMutation = useMutation(
    ({ id, email }: { id: string; email: string }) =>
      apiService.removeFeatureRequestTeamMember(id, email),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("feature-request-teams");
        toast.success("Member removed");
      },
      onError,
    },
  );

  const addPowerUserMutation = useMutation(
    () => apiService.addFeatureRequestPowerUser(newEmail.trim()),
    {
      onSuccess: () => {
        setNewEmail("");
        queryClient.invalidateQueries("feature-request-power-users");
        toast.success("Power user added");
      },
      onError,
    },
  );

  const removePowerUserMutation = useMutation(
    (id: string) => apiService.removeFeatureRequestPowerUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("feature-request-power-users");
        toast.success("Power user removed");
      },
      onError,
    },
  );

  const handleDeleteTopic = (slug: string, label: string, inUse: number) => {
    if (inUse > 0) {
      const confirmed = window.confirm(
        `"${label}" is used by ${inUse} request${inUse === 1 ? "" : "s"}. ` +
          "They will be moved to Other. Continue?",
      );
      if (!confirmed) return;
      deleteTopicMutation.mutate({ slug, reassign: true });
      return;
    }
    deleteTopicMutation.mutate({ slug, reassign: false });
  };

  return (
    <div className="space-y-6">
      <Section
        icon={Users}
        title="Teams"
        description="A team owns a set of topics, and its members can manage every request in them. Only the team's creator can rename it, delete it or change who is in it."
      >
        <form
          className="mb-5 flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (newTeam.trim()) createTeamMutation.mutate();
          }}
        >
          <input
            type="text"
            value={newTeam}
            onChange={(e) => setNewTeam(e.target.value)}
            placeholder="New team name, e.g. DevX"
            maxLength={50}
            className="input sm:flex-1"
            disabled={createTeamMutation.isLoading}
          />
          <button
            type="submit"
            className="btn btn-primary shrink-0 gap-1.5"
            disabled={!newTeam.trim() || createTeamMutation.isLoading}
          >
            <Plus className="h-4 w-4" />
            Create team
          </button>
        </form>

        {teamsLoading ? (
          <LoadingSpinner />
        ) : teams.length === 0 ? (
          <EmptyState>
            No teams yet. Create one, then tag your topics into it.
          </EmptyState>
        ) : (
          <div className="space-y-3">
            {teams.map((team) => {
              const members = team.members ?? [];
              const topicCount = (team.topics ?? []).length;
              return (
                <div
                  key={team.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {team.name}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                          {topicCount} topic{topicCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                        Created by {team.createdByEmail}
                      </p>
                    </div>
                    {team.canEdit && (
                      <DeleteButton
                        onClick={() => deleteTeamMutation.mutate(team.id)}
                        disabled={deleteTeamMutation.isLoading}
                        label={`Delete team ${team.name}`}
                      />
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {members.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                      >
                        {email}
                        {team.canEdit && (
                          <button
                            type="button"
                            onClick={() =>
                              removeMemberMutation.mutate({
                                id: team.id,
                                email,
                              })
                            }
                            className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900 dark:hover:text-red-400"
                            aria-label={`Remove ${email} from ${team.name}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    {members.length === 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        No members yet
                      </span>
                    )}
                  </div>

                  {team.canEdit && (
                    <form
                      className="mt-3 flex flex-col gap-2 sm:flex-row"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const email = (memberDrafts[team.id] ?? "").trim();
                        if (email)
                          addMemberMutation.mutate({ id: team.id, email });
                      }}
                    >
                      <input
                        type="email"
                        value={memberDrafts[team.id] ?? ""}
                        onChange={(e) =>
                          setMemberDrafts((d) => ({
                            ...d,
                            [team.id]: e.target.value,
                          }))
                        }
                        placeholder="Add member by email"
                        className="input h-9 sm:flex-1"
                      />
                      <button
                        type="submit"
                        className="btn btn-secondary h-9 shrink-0 gap-1.5"
                        disabled={
                          !(memberDrafts[team.id] ?? "").trim() ||
                          addMemberMutation.isLoading
                        }
                      >
                        <UserPlus className="h-4 w-4" />
                        Add member
                      </button>
                    </form>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section
        icon={Tag}
        title="Topics"
        description="Topics are backed by GitHub labels, so they can also be managed from GitHub. You can only change or move topics you own. Removing a topic moves its requests to Other."
      >
        <form
          className="mb-5 flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (newTopic.trim()) createTopicMutation.mutate();
          }}
        >
          <input
            type="text"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            placeholder="New topic name"
            maxLength={50}
            className="input sm:flex-1"
            disabled={createTopicMutation.isLoading}
          />
          <select
            value={newTopicTeam}
            onChange={(e) => setNewTopicTeam(e.target.value)}
            className="input sm:w-48"
            aria-label="Team for the new topic"
          >
            <option value="">No team</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn btn-primary shrink-0 gap-1.5"
            disabled={!newTopic.trim() || createTopicMutation.isLoading}
          >
            <Plus className="h-4 w-4" />
            Add topic
          </button>
        </form>

        {topicsLoading ? (
          <LoadingSpinner />
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {topics.map((topic) =>
              editingTopic === topic.slug ? (
                <li key={topic.slug} className="py-3">
                  <form
                    className="flex flex-wrap items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (editDraft.name.trim())
                        updateTopicMutation.mutate({
                          slug: topic.slug,
                          name: editDraft.name.trim(),
                          color: editDraft.color,
                        });
                    }}
                  >
                    <input
                      type="color"
                      value={`#${editDraft.color || "1f6feb"}`}
                      onChange={(e) =>
                        setEditDraft((d) => ({
                          ...d,
                          color: e.target.value.replace("#", ""),
                        }))
                      }
                      className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800"
                      aria-label={`Colour for ${topic.label}`}
                    />
                    <input
                      type="text"
                      value={editDraft.name}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, name: e.target.value }))
                      }
                      maxLength={50}
                      autoFocus
                      className="input h-9 min-w-0 flex-1"
                      aria-label={`Name for ${topic.label}`}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary h-9 shrink-0 gap-1.5"
                      disabled={
                        !editDraft.name.trim() || updateTopicMutation.isLoading
                      }
                    >
                      <Check className="h-4 w-4" />
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingTopic(null)}
                      className="btn btn-secondary h-9 shrink-0 gap-1.5"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </button>
                  </form>
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    Renaming changes the display name only; the identifier
                    <code className="mx-1 rounded bg-gray-100 px-1 dark:bg-gray-700">
                      {topic.slug}
                    </code>
                    stays fixed, so existing requests keep their topic.
                  </p>
                </li>
              ) : (
                <li
                  key={topic.slug}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: topic.color
                          ? `#${topic.color}`
                          : "#d1d5db",
                      }}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {topic.label}
                      </div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {topic.inUse > 0
                          ? `${topic.inUse} request${topic.inUse === 1 ? "" : "s"}`
                          : "No requests"}
                        {" · "}
                        {topic.ownerEmail ? topic.ownerEmail : "Unowned"}
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <select
                      value={topic.teamId ?? ""}
                      onChange={(e) =>
                        assignTeamMutation.mutate({
                          slug: topic.slug,
                          teamId: e.target.value || null,
                        })
                      }
                      disabled={!topic.canEdit || assignTeamMutation.isLoading}
                      className="input h-9 w-40 text-xs"
                      aria-label={`Team for ${topic.label}`}
                    >
                      <option value="">No team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                    {topic.canEdit && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingTopic(topic.slug);
                          setEditDraft({
                            name: topic.label,
                            color: topic.color || "1f6feb",
                          });
                        }}
                        className="btn-icon shrink-0"
                        aria-label={`Edit topic ${topic.label}`}
                        title={`Edit topic ${topic.label}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {topic.slug !== "other" && topic.canEdit && (
                      <DeleteButton
                        onClick={() =>
                          handleDeleteTopic(
                            topic.slug,
                            topic.label,
                            topic.inUse,
                          )
                        }
                        disabled={deleteTopicMutation.isLoading}
                        label={`Remove topic ${topic.label}`}
                      />
                    )}
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
      </Section>

      <Section
        icon={ShieldCheck}
        title="Power users"
        description="Power users can create teams and topics. What they can actually manage comes from the teams they belong to. Only super admins can grant or revoke these rights."
      >
        {isSuperAdmin && (
          <form
            className="mb-5 flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              if (newEmail.trim()) addPowerUserMutation.mutate();
            }}
          >
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="name@sandvik.com"
              className="input sm:flex-1"
              disabled={addPowerUserMutation.isLoading}
            />
            <button
              type="submit"
              className="btn btn-primary shrink-0 gap-1.5"
              disabled={!newEmail.trim() || addPowerUserMutation.isLoading}
            >
              <UserPlus className="h-4 w-4" />
              Add power user
            </button>
          </form>
        )}

        {powerUsersQuery.isLoading ? (
          <LoadingSpinner />
        ) : (powerUsersQuery.data ?? []).length === 0 ? (
          <EmptyState>
            No power users yet. Super admins always have full access.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {(powerUsersQuery.data ?? []).map((pu) => (
              <li
                key={pu.id}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {pu.email}
                  </div>
                  <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                    Granted by {pu.grantedByEmail} on{" "}
                    {new Date(pu.grantedAt).toLocaleDateString("en-GB")}
                  </div>
                </div>
                {isSuperAdmin && (
                  <DeleteButton
                    onClick={() => removePowerUserMutation.mutate(pu.id)}
                    disabled={removePowerUserMutation.isLoading}
                    label={`Remove power user ${pu.email}`}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
